import { Box, CircularProgress, Grid } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams, useSearchParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { AIAgent } from 'components/AIAgent';
import { useAppSelector } from 'components/app/redux/store';

import {
  checkIsButtonDisplayed,
  deleteSavedQuery,
  editQuery,
  getSavedQuery,
  getSpentTimeFilters,
  getSpentTimes,
  getTimesheetCsv,
  saveQuery,
} from '../../../../packages/common/api';
import ChatBot from '../../../../packages/common/shared-ui/src/components/ChatBot';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';
import {
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  formattedParamsData,
  paramsToQuery,
  queryToParams,
} from '../../../../packages/common/utils/gridFiltersHelperNew';
import CommonPage from '../../CommonPage/CommonPage';
import GridFiltersModalNew from '../../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../../GridSaveQueryModal/GridSaveQueryModal';
import GridTotals from '../../GridTotals/GridTotals';
import SpentTimeTable from '../SpentTimeTable';
import TimesheetsCreateEditModal from '../TimesheetsCreateEditModal/TimesheetsCreateEditModal';

const SpentTimeContainer = () => {
  const getLocalString = useTranslation();
  const featureFlags = useAppSelector(state => state.configuration.features);
  const navigate = useNavigate();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const pageUseParams = useParams();
  const [isAddButtonDisplayed, setIsAddButtonDisplayed] = useState(false);
  const [issuesGridRows, setIssuesGridRows] = useState([]);
  const [issuesGridColumns, setIssuesGridColumns] = useState([]);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [openCreateEditModal, setCreateEditModal] = useState(false);
  const [createModalState, setCreateModalState] = useState(null);
  const [queryData, setQueryData] = useState(null);
  const [totals, setTotals] = useState([]);
  const [query, setQuery] = useQueryParams({
    groups: StringParam,
    filters: StringParam,
    totals: StringParam,
    sorts: StringParam,
    fields: StringParam,
    limit: NumberParam,
    offset: NumberParam,
  });
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [queryModalState, setQueryModalState] = useState(null);
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate('/timesheets');
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate],
  );
  useEffect(() => {
    let ignore = false;
    async function canAdd() {
      const response = await checkIsButtonDisplayed();
      if (!ignore) {
        return response;
      }
    }
    canAdd()
      .then(res => {
        const { data: canAdd } = res;
        setIsAddButtonDisplayed(canAdd);
      })
      .catch(error => handleError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleError]);
  //Получение фильтров
  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    pageUseParams.projectId && (params.project = pageUseParams.projectId);
    async function getParamsData() {
      const response = await getSpentTimeFilters(params);
      if (!ignore) {
        return response;
      }
    }

    getParamsData()
      .then(res => {
        const { data: paramsData } = res;
        const params = formatFilters(paramsData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
  }, [pageUseParams.queryId]);

  //Получение query по id
  useEffect(() => {
    setQueryData(null);
    let ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries()
        .then(res => {
          res && setQueryData(res);
        })
        .catch(error => handleError(error.response));
    }
  }, [handleError, pageUseParams.queryId]);

  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);

  const getTimesheets = useCallback(() => {
    setParamsLoading(true);
    let params = query;
    pageUseParams.projectId &&
      (params.project = Number(pageUseParams.projectId));
    async function getData() {
      return await getSpentTimes(params);
    }
    getData()
      .then(res => {
        const { data: issuesData } = res;
        issuesData.fields && setIssuesGridColumns(issuesData.fields);
        issuesData.rows && setIssuesGridRows(issuesData.rows);
        issuesData.paging && setPagination(issuesData.paging);
        const totalRow = issuesData.rows.find(item => item.rowType === 'total');
        totalRow !== undefined && totalRow.columns
          ? setTotals(totalRow.columns)
          : setTotals([]);
        setLoading(false);
        setParamsLoading(false);
      })
      .catch(error => handleError(error.response));
  }, [handleError, pageUseParams.projectId, query]);
  useEffect(() => {
    const queryIsSet =
      Object.values(query).filter(item => item !== undefined).length > 0;
    queryIsSet && getTimesheets();
  }, [getTimesheets, query]);

  const handleSelectFilters = useCallback(
    data => {
      if (!data.hasOwnProperty('operation')) {
        return;
      }
      const items = currentParams.filters.selectedItems.map(item => {
        if (item.fieldId === data.fieldId) {
          return data;
        } else {
          return item;
        }
      });
      const updatedParams = {
        ...currentParams,
        filters: { ...currentParams.filters, selectedItems: items },
      };
      setCurrentParams(updatedParams);
      setQuery(paramsToQuery(updatedParams));
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
    },
    [setQuery],
  );
  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);
  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);
  const handleChangePagination = useCallback(
    data => {
      setQuery(prev => ({ ...prev, offset: data * 100 }));
    },
    [setQuery],
  );
  const handleCreateEditModalClose = useCallback(() => {
    setCreateEditModal(false);
    setCreateModalState(null);
  }, []);
  const handleCreateEditModalSave = useCallback(() => {
    setCreateEditModal(false);
    setCreateModalState(null);
    getTimesheets();
  }, [getTimesheets]);

  const handleChangeSorting = useCallback(
    newSortModel => {
      if (!newSortModel || newSortModel.length === 0) {
        setQuery(prev => ({ ...prev, sorts: undefined }));
        return;
      }

      const sortString = newSortModel
        .map(item => `${item.field};${item.sort}`)
        .join('$$');

      setQuery(prev => ({ ...prev, sorts: sortString }));
    },
    [setQuery],
  );

  const handleGetRedirectUrl = useCallback(({ id, columnType }) => {
    switch (columnType) {
      case 'project':
        return `${window.location.origin}/projects/${id}`;
      case 'issue':
        return generateRedirectUrl({
          host: window.location.origin,
          path: `/issues/${id}`,
        });
      default:
        return '';
    }
  }, []);

  const [contextMenuData, setContextMenuData] = useState({
    isSecondStepVisible: false,
    secondPopoverStepValue: null,
    currentRow: {
      services: {
        isApproved: '',
        isOvertime: false,
        canApprove: false,
        canEdit: false,
        isProjectClosed: false,
      },
    },
  });
  const downloadCsv = useCallback(() => {
    async function downloadTimesheetCsv() {
      try {
        setDownloadLoading(true);
        let params = { ...query };
        pageUseParams.projectId && (params.project = pageUseParams.projectId);
        //let query = searchParams.toString();
        //query = query.replaceAll(/ThisWeek|LastWeek|LastTwoWeeks|ThisMonth|LastMonth|ThisYear/gi, 'btw');
        //query = query.replaceAll('Today', 'eq');
        //pageUseParams.queryId && (query = `query=${Number(pageUseParams.queryId)}&` + query);
        //pageUseParams.projectId && (query = `project=${pageUseParams.projectId}&` + query);
        if (params) {
          const { data } = await getTimesheetCsv(params);
          const url = window.URL.createObjectURL(
            new Blob(['\ufeff', data], { type: 'text/csv;charset=utf-8;' }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `spentTime-${dayjs().format('YYYY-MM-DD')}`,
          );
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          link.remove();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDownloadLoading(false);
      }
    }
    downloadTimesheetCsv();
  }, [pageUseParams.projectId, query]);

  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'timesheet');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          if (pageUseParams.projectId) {
            window.open(
              `/projects/${pageUseParams.projectId}/timesheets/query/${res.id}`,
              '_self',
            );
          } else {
            window.open(`/timesheets/query/${res.id}`, '_self');
          }
        }
      } catch (error) {
        handleError(error.response);
      }
      setOpenSaveQueryModal(false);
    },
    [
      currentParams,
      handleError,
      pageUseParams.queryId,
      pageUseParams.projectId,
      queryModalState,
    ],
  );

  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.ADD:
          //const url = pageUseParams.projectId
          //  ? `/projects/${pageUseParams.projectId}/timesheets/create`
          //  : `/timesheets/create`;
          //window.open(url, '_blank');
          setCreateModalState('create');
          setCreateEditModal(true);
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
        case ButtonsTypes.QUERY:
          setQueryModalState('create');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.EDIT_QUERY:
          setQueryModalState('edit');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.DELETE_QUERY:
          setOpenWarningModal(true);
          break;
        case ButtonsTypes.DOWNLOAD:
          downloadCsv();
          break;
      }
    },
    [downloadCsv],
  );
  const handleEditEvent = useCallback(data => {
    if (Array.isArray(data)) {
      setCreateModalState('bulk_edit');
    } else {
      setCreateModalState('edit');
    }
    setModalData(data);
    setCreateEditModal(true);
  }, []);
  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);

  const handleApproveOrRejectTimesheets = useCallback(() => {
    getTimesheets();
  }, [getTimesheets]);
  const handleApproveOrRejectOvertime = useCallback(() => {
    getTimesheets();
  }, [getTimesheets]);
  const handleDeleteTimesheet = useCallback(() => {
    getTimesheets();
  }, [getTimesheets]);
  return (
    <>
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      <GridSaveQueryModal
        state={queryModalState}
        saveLoader={saveLoader}
        permission={'canSaveTimesheetsGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
      />
      <TimesheetsCreateEditModal
        open={openCreateEditModal}
        state={createModalState}
        handleClose={handleCreateEditModalClose}
        handleSave={handleCreateEditModalSave}
        data={modalData}
        project={pageUseParams.projectId ? pageUseParams.projectId : null}
      ></TimesheetsCreateEditModal>
      <CommonPage
        loading={loading}
        canCreate={isAddButtonDisplayed}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        downloadLoading={downloadLoading}
        component={'SpentTimeContainer'}
      >
        <>
          {currentParams &&
            currentParams.filters?.selectedItems?.length > 0 && (
              <Box sx={{ marginTop: '10px' }}>
                <GridFiltersPanelNew
                  data={currentParams.filters.selectedItems}
                  handleCloseFilter={handleSelectFilters}
                ></GridFiltersPanelNew>
              </Box>
            )}
          {totals.length > 0 && <GridTotals data={totals}></GridTotals>}
          {issuesGridColumns && issuesGridRows && (
            <Grid
              container
              wrap='nowrap'
              sx={{
                overflow: 'hidden',
                flexDirection: 'column',
                position: 'relative',
                marginTop: totals.length > 0 ? 0 : '20px',
              }}
            >
              {paramsLoading && (
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    zIndex: 99,
                    position: 'absolute',
                  }}
                >
                  <CircularProgress color='secondary' />
                </Grid>
              )}
              {featureFlags.CHATBOT &&
                (featureFlags.CHATBOT_VOICE_INPUT ? <AIAgent /> : <ChatBot />)}
              <SpentTimeTable
                contextMenuData={contextMenuData}
                setContextMenuData={setContextMenuData}
                columnsData={issuesGridColumns}
                rowsData={issuesGridRows}
                changePagination={handleChangePagination}
                changeSorting={handleChangeSorting}
                paginationData={pagination}
                editEvent={handleEditEvent}
                approveOrRejectTimesheets={handleApproveOrRejectTimesheets}
                approveOrRejectOvertime={handleApproveOrRejectOvertime}
                deleteTimesheet={handleDeleteTimesheet}
              />
            </Grid>
          )}
        </>
      </CommonPage>
    </>
  );
};

export default SpentTimeContainer;
